import React, { useCallback, useMemo, useState } from 'react';
import { Page, pdfjs } from 'react-pdf';
import { RenderMode } from 'react-pdf/dist/cjs/shared/types';
import { MobileRenderer } from 'Components/DocumentPage';
import OverlayLoader from 'Components/UIComponents/UIOverlayLoader';
import { useIsMobile, useIsTablet, useToggler } from 'Hooks/Common';
import { DocumentField } from 'Interfaces/DocumentFields';

import DocumentFieldPreviewItem from './DocumentFieldPreviewItem';

export interface DocumentNavigationItemProps {
  navigateToPage: () => void;
  pageNumber: number;
  pageMeta?: {
    width: number;
    height: number;
  } | null;
  fields: DocumentField[];
}

function DocumentNavigationItem({
  pageNumber,
  navigateToPage,
  pageMeta,
  fields,
}: DocumentNavigationItemProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [renderMode, setRenderMode] = useState<RenderMode>(
    isMobile || isTablet ? 'custom' : 'canvas',
  );
  const [scale, setScale] = useState(1);
  const [isLoading, toggleIsLoading] = useToggler(true);

  const pageDimensions = useMemo(
    () => ({
      width: (pageMeta?.width || 0) * scale,
      height: (pageMeta?.height || 0) * scale,
    }),
    [pageMeta, scale],
  );

  const handleLoadSuccess = useCallback(
    async page => {
      const operatorList = await page.getOperatorList();

      const isSomeImage = operatorList.fnArray.some(
        fn => fn === pdfjs.OPS.paintImageXObject,
      );

      if (isSomeImage) {
        setRenderMode('canvas');
      }

      toggleIsLoading();
    },
    [toggleIsLoading],
  );

  const setContainerRef = useCallback(
    (element: HTMLLIElement) => {
      if (!element || !pageMeta?.width || !pageMeta?.height) return element;

      setScale(element.clientWidth / pageMeta.width);

      return element;
    },
    [pageMeta?.height, pageMeta?.width],
  );

  return (
    <li
      ref={setContainerRef}
      className="interactModal__documentNavigation-item"
      onClick={navigateToPage}
    >
      {isLoading && (
        <OverlayLoader
          iconClassName="overlayLoader__icon--preview"
          labelClassName="overlayLoader__text--preview"
        />
      )}
      <Page
        pageNumber={pageNumber}
        onLoadSuccess={handleLoadSuccess}
        {...pageDimensions}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        renderMode={renderMode}
        customRenderer={isMobile || isTablet ? MobileRenderer : undefined}
      />
      {fields.map(field => (
        <DocumentFieldPreviewItem key={field.id} field={field} scale={scale} />
      ))}
    </li>
  );
}

export default DocumentNavigationItem;
